import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Banner, Toast } from '../components';
import { ConfirmDialog } from './ConfirmDialog';

/** What the action asks before it runs; the toast is dismissed and the dialog outlives the page. */
export interface ToastConfirm {
  title: ReactNode;
  body?: ReactNode;
  confirmLabel: string;
  tone?: 'critical' | 'primary';
  items: { name: ReactNode; meta?: ReactNode }[];
  itemsLabel?: ReactNode;
}

interface ToastOptions {
  actionLabel?: string;
  onAction?: () => void | Promise<void>;
  confirm?: ToastConfirm;
  /** A new toast of a group replaces the group's earlier ones, so only the latest offers its action. */
  group?: string;
}

interface ToastEntry extends ToastOptions {
  id: number;
  message: ReactNode;
}

type Notify = (message: ReactNode, options?: ToastOptions) => void;

/** A refused toast action (Undo), held where the toast was until dismissed. */
export interface ToastRefusal {
  title: ReactNode;
  reason?: string;
  detail?: ReactNode;
}

type Refuse = (refusal: ToastRefusal) => void;

const ToastContext = createContext<Notify>(() => undefined);
const RefusalContext = createContext<Refuse>(() => undefined);

const DURATION_MS = 6000;
const DIALOG = '[role="alertdialog"], [role="dialog"]';

/** What a toast has left to show; `handle` is set only while its timer runs. */
interface Clock {
  left: number;
  since: number;
  handle: number | null;
}

/** Success confirmations, with Undo where one exists; a refused Undo stays where the toast was, as a Banner. */
export function Toaster({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const [refusal, setRefusal] = useState<ToastRefusal | null>(null);
  const [asking, setAsking] = useState<{
    confirm: ToastConfirm;
    run: () => void | Promise<void>;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const seq = useRef(0);
  const stack = useRef<HTMLDivElement>(null);
  const clocks = useRef(new Map<number, Clock>());
  const shown = useRef<number[]>([]);
  const groups = useRef(new Map<number, string>());
  const holds = useRef({ pointer: false, focus: false, dialog: false });

  const free = useCallback((id: number) => {
    const clock = clocks.current.get(id);
    if (clock?.handle != null) window.clearTimeout(clock.handle);
    clocks.current.delete(id);
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      free(id);
      shown.current = shown.current.filter((shownId) => shownId !== id);
      groups.current.delete(id);
      setToasts((all) => all.filter((t) => t.id !== id));
    },
    [free],
  );

  // Every timer stops while the operator is reaching for the stack or a dialog is open, and resumes with its time left.
  const sync = useCallback(() => {
    const { pointer, focus, dialog } = holds.current;
    const held = pointer || focus || dialog;
    const now = Date.now();
    for (const [id, clock] of clocks.current) {
      if (held && clock.handle != null) {
        window.clearTimeout(clock.handle);
        clocks.current.set(id, {
          left: clock.left - (now - clock.since),
          since: now,
          handle: null,
        });
      } else if (!held && clock.handle == null) {
        const handle = window.setTimeout(() => dismiss(id), clock.left);
        clocks.current.set(id, { left: clock.left, since: now, handle });
      }
    }
  }, [dismiss]);

  const notify = useCallback<Notify>(
    (message, options) => {
      const id = ++seq.current;
      if (options?.group) {
        for (const [earlier, group] of [...groups.current])
          if (group === options.group) dismiss(earlier);
        groups.current.set(id, options.group);
      }
      for (const pushedOut of shown.current.slice(0, -2)) {
        free(pushedOut);
        groups.current.delete(pushedOut);
      }
      shown.current = [...shown.current.slice(-2), id];
      setToasts((all) => [...all.slice(-2), { id, message, ...options }]);
      clocks.current.set(id, { left: DURATION_MS, since: 0, handle: null });
      sync();
    },
    [dismiss, free, sync],
  );

  useEffect(() => {
    const check = () => {
      holds.current.dialog = !!document.querySelector(DIALOG);
      sync();
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [sync]);

  // A removed toast takes its pointer or focus with it without an event; read both again.
  useEffect(() => {
    const el = stack.current;
    holds.current.pointer = !!el?.matches(':hover');
    holds.current.focus = !!el?.contains(document.activeElement);
    sync();
  }, [toasts, refusal, sync]);

  useEffect(() => {
    const all = clocks.current;
    return () => {
      for (const clock of all.values()) if (clock.handle != null) window.clearTimeout(clock.handle);
      all.clear();
    };
  }, []);

  const confirmAsked = async () => {
    if (!asking || running.current) return;
    running.current = true;
    setBusy(true);
    try {
      await asking.run();
    } finally {
      running.current = false;
      setBusy(false);
      setAsking(null);
    }
  };

  const value = useMemo(() => notify, [notify]);

  return (
    <ToastContext.Provider value={value}>
      <RefusalContext.Provider value={setRefusal}>
        {children}
        <div
          className="rq-toasts"
          ref={stack}
          onPointerEnter={() => {
            holds.current.pointer = true;
            sync();
          }}
          onPointerLeave={() => {
            holds.current.pointer = false;
            sync();
          }}
          onFocus={() => {
            holds.current.focus = true;
            sync();
          }}
          onBlur={(event) => {
            holds.current.focus = event.currentTarget.contains(event.relatedTarget);
            sync();
          }}
        >
          {refusal ? (
            <div className="rq-toast-refusal">
              <Banner
                tone="critical"
                title={refusal.title}
                reason={refusal.reason}
                onDismiss={() => setRefusal(null)}
              >
                {refusal.detail}
              </Banner>
            </div>
          ) : null}
          {toasts.map((t) => (
            <Toast
              key={t.id}
              actionLabel={t.onAction ? (t.actionLabel ?? 'Undo') : null}
              onAction={() => {
                if (t.confirm && t.onAction) setAsking({ confirm: t.confirm, run: t.onAction });
                else void t.onAction?.();
                dismiss(t.id);
              }}
              onDismiss={() => dismiss(t.id)}
            >
              {t.message}
            </Toast>
          ))}
        </div>
        {asking ? (
          <ConfirmDialog
            title={asking.confirm.title}
            tone={asking.confirm.tone}
            items={asking.confirm.items}
            itemsLabel={asking.confirm.itemsLabel}
            confirmLabel={asking.confirm.confirmLabel}
            busy={busy}
            onConfirm={() => void confirmAsked()}
            onCancel={() => !running.current && setAsking(null)}
          >
            {asking.confirm.body}
          </ConfirmDialog>
        ) : null}
      </RefusalContext.Provider>
    </ToastContext.Provider>
  );
}

export const useToast = (): Notify => useContext(ToastContext);

/** Shows a refused toast action as a Banner where the toast was. */
export const useToastRefusal = (): Refuse => useContext(RefusalContext);
